// components/profile/NameVerificationSection.tsx
import React, { useEffect, useRef, useState } from 'react';
import { User, CheckCircle, Upload, AlertCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import type { User as UserProfile } from '../../types';
import { showToast } from '../../utils/toast';
import userAPI from '../../api/user';
import { formatDate, formatDateTime } from '../../utils/date';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface NameVerificationSectionProps {
  user: UserProfile;
  onNameSubmit: (data: { first_name: string; last_name: string; middle_name?: string }) => Promise<void>;
  onIdUpload: (file: File) => Promise<void>;
}

export const NameVerificationSection: React.FC<NameVerificationSectionProps> = ({
  user,
  onNameSubmit,
  onIdUpload
}) => {
  const { refreshUser, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<File | null>(null);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [middleName, setMiddleName] = useState(user?.middle_name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idFile, setIdFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isIdModalOpen, setIsIdModalOpen] = useState(false);
  const [idImageUrl, setIdImageUrl] = useState<string | null>(null);

  useEffect(() => {
    // Refresh user data when component mounts
    refreshUser();
  }, []);

useEffect(() => {
  if (!idFile) {
    fileRef.current = null;
  }
}, [idFile]);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateNameForm()) return;

    setIsSubmitting(true);
    try {
      await onNameSubmit({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        middle_name: middleName.trim() || undefined
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("FILE SELECTED");
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        alert('Please upload a JPG, PNG, or PDF file');
        return;
      }

      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return;
      }

      setIdFile(file);
    }
  };

  const handleIdUpload = async () => {
    if (!idFile) return;

    setUploadProgress(10);
    try {
      await onIdUpload(idFile);
      setUploadProgress(100);
      setIdFile(null);
      const fileInput = document.getElementById('id-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      setUploadProgress(0);
      showToast('Failed to upload ID', 'error');
    }
  };

  const shouldAllowReupload = () => {
    if (user.id_verification_status === 'rejected') return true;

    if (user.id_verification_status === 'pending' && user.id_uploaded_at) {
      const uploadedDate = new Date(user.id_uploaded_at);
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      return uploadedDate < threeDaysAgo;
    }

    return false;
  };
  
  const allowReupload = shouldAllowReupload();

  const handleViewIdDocument = async () => {
    try {
      const blob = await userAPI.viewIdDocument();
      const url = window.URL.createObjectURL(blob);

      // Check screen size
      if (window.innerWidth >= 768) { // tablet/desktop
        setIdImageUrl(url);
        setIsIdModalOpen(true);
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      showToast('Failed to load ID document', 'error');
    }
  };

  const handleReplaceIdUpload = async () => {
    if (!idFile) return;
    setUploadProgress(10);
    try {
      // Use replaceIdDocument instead of onIdUpload
      const response = await userAPI.replaceIdDocument(idFile);

      if (response.data) {
        setUploadProgress(100);
        setIdFile(null);

        // Update user state with new upload
        updateUser?.({
          id_uploaded_at: response.data.uploaded_at,
          id_verification_status: response.data.verification_status as 'not_uploaded' | 'pending' | 'verified' | 'rejected',
        });

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        showToast(response.data.message || 'ID replaced successfully', 'success');
      } else if (response.error) {
        setUploadProgress(0);
        showToast(response.error, 'error');
      }
    } catch (error) {
      setUploadProgress(0);
      showToast('Failed to replace ID', 'error');
    }
  };

  const validateNameForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (firstName.length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }

    if (!lastName.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (lastName.length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }

    if (user?.is_id_verified) {
      if (firstName !== user.first_name) {
        newErrors.first_name = 'Cannot change first name after ID verification';
      }
      if (lastName !== user.last_name) {
        newErrors.last_name = 'Cannot change last name after ID verification';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const renderFileUpload = () => (
    <div className="space-y-4">
      {!user.id_document_path && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose ID Document
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                id="id-file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {idFile ? idFile.name : 'Click to upload ID'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, or PDF (max 5MB)
                </p>
              </div>
            </label>

            {idFile && (
              <button
                onClick={handleIdUpload}
                disabled={uploadProgress > 0}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
              >
                {uploadProgress > 0 ? 'Uploading...' : 'Upload ID'}
              </button>
            )}
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-2 text-center">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // STATE 1: FULLY VERIFIED
  if (user.is_id_verified) {
    return (
      <div className="bg-white rounded-lg border p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center mb-3 sm:mb-0">
            <div className="p-3 bg-green-100 rounded-full mr-4 flex-shrink-0">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {user.first_name} {user.last_name}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-1 flex-shrink-0" />
                  <span className="text-sm text-green-600 font-medium">Identity Verified</span>
                </div>
                {user.id_verified_at && (
                  <>
                    <span className="hidden sm:inline text-gray-300">•</span>
                    <span className="text-sm text-gray-500">
                      Verified on {formatDate(user.id_verified_at!)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full w-fit sm:w-auto">
            Trusted Member
          </span>
        </div>
      </div>
    );
  }

  // STATE 2: NAME SET + ID PENDING VERIFICATION
  if (user.first_name && user.last_name && user.id_verification_status === 'pending') {
    return (
      <>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleFileChange}
          className="hidden"
          key="file-input" // Add key to ensure it's unique
        />
        <div className="bg-white rounded-lg border p-4 sm:p-6 mb-6">
          <div className="flex items-start">
            <div className="p-3 bg-yellow-100 rounded-full mr-4 flex-shrink-0">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {user.first_name} {user.last_name}
              </h2>
              <p className="text-sm text-gray-600">
                ID verification pending review
              </p>
              {user.id_uploaded_at && (
                <p className="text-xs text-gray-500 mt-1">
                  Uploaded {formatDistanceToNow(new Date(user.id_uploaded_at))} ago - {formatDateTime(user.id_uploaded_at)}
                </p>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">Verification in Progress</p>
                <p className="text-sm text-yellow-600 mt-1">
                  Your ID document is under review. This usually takes 1-2 business days.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={handleViewIdDocument}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Uploaded ID
                  </button>
                  {shouldAllowReupload() && (
                    <>
                      <span className="text-gray-300 hidden sm:inline">•</span>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Upload different file
                      </button>
                    </>)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {allowReupload && idFile && (
          <div className="mt-4 border-t pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">New file selected:</p>
                <p className="text-sm text-gray-500 truncate">{idFile.name}</p>
                <p className="text-xs text-gray-400 mt-1">
                  This will replace your previously uploaded ID
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIdFile(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReplaceIdUpload}
                  disabled={uploadProgress > 0}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {uploadProgress > 0 ? 'Uploading...' : 'Replace ID'}
                </button>
              </div>
            </div>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        )}
        {/* Modal for ID viewing */}
        {isIdModalOpen && idImageUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center border-b px-6 py-4">
                <h3 className="text-lg font-semibold">ID Document</h3>
                <button
                  onClick={() => {
                    setIsIdModalOpen(false);
                    URL.revokeObjectURL(idImageUrl);
                    setIdImageUrl(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="p-6 overflow-auto max-h-[calc(90vh-80px)]">
                <img
                  src={idImageUrl}
                  alt="ID Document"
                  className="max-w-full h-auto mx-auto"
                  onLoad={() => URL.revokeObjectURL(idImageUrl)} // Clean up after load
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // STATE 3: NAME SET + ID REJECTED
  if (user.first_name && user.last_name && user.id_verification_status === 'rejected') {
    return (
      <div className="bg-white rounded-lg border p-4 sm:p-6 mb-6">
        <div className="flex items-start">
          <div className="p-3 bg-red-100 rounded-full mr-4 flex-shrink-0">
            <XCircle className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-sm text-red-600 font-medium">
              ID verification failed
            </p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800 font-medium">Verification Required</p>
              <p className="text-sm text-red-600 mt-1">
                Your ID document was rejected. Please upload a new, clearer image.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Upload New ID Document</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Requirements</p>
                <p className="text-sm text-blue-600 mt-1">
                  Upload a clear photo of your government-issued ID.
                  Name must match: <strong>{user.first_name} {user.last_name}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* File upload section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose ID Document
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    id="id-file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {idFile ? idFile.name : 'Click to upload ID'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG, or PDF (max 5MB)
                    </p>
                  </div>
                </label>

                {idFile && (
                  <button
                    onClick={handleIdUpload}
                    disabled={uploadProgress > 0}
                    className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {uploadProgress > 0 ? 'Uploading...' : 'Upload ID'}
                  </button>
                )}
              </div>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-4">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STATE 4: NAME SET + NO ID UPLOADED
  if (user.first_name && user.last_name && !user.id_document_path) {
    return (
      <div className="bg-white rounded-lg border p-4 sm:p-6 mb-6">
        <div className="flex items-start">
          <div className="p-3 bg-blue-100 rounded-full mr-4 flex-shrink-0">
            <User className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
              {user.first_name} {user.middle_name || ''} {user.last_name}
            </h2>
            <p className="text-sm text-gray-600">
              Name saved, ready for ID upload
            </p>
          </div>
        </div>

        <div className="border-t pt-6 mt-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Upload ID for Verification</h3>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Final Step Required</p>
                <p className="text-sm text-blue-600 mt-1">
                  Upload a clear photo of your government-issued ID.
                  Name must match: <strong>{user.first_name} {user.last_name}</strong>
                </p>
              </div>
            </div>
          </div>

          {renderFileUpload()}
        </div>
      </div>
    );
  }

  // STATE 5: NO NAME SET (Initial state)
  return (
    <div className="bg-white rounded-lg border p-4 sm:p-6 mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Verify Your Identity</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Verification Required</p>
            <p className="text-sm text-blue-600 mt-1">
              To place orders, verify your identity with your legal name and government ID.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleNameSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
              Legal First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={firstName}
              onChange={(e) => {
                setErrors(prev => ({ ...prev, first_name: '' }));
                setFirstName(e.target.value);
              }}
              className={`w-full border rounded-lg px-4 py-3 text-sm ${errors.first_name ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="As it appears on your government ID"
            />
            {errors.first_name && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                {errors.first_name}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
              Legal Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={lastName}
              onChange={(e) => {
                setErrors(prev => ({ ...prev, last_name: '' }));
                setLastName(e.target.value);
              }}
              className={`w-full border rounded-lg px-4 py-3 text-sm ${errors.last_name ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="As it appears on your government ID"
            />
            {errors.last_name && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                {errors.last_name}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="middle_name" className="block text-sm font-medium text-gray-700 mb-2">
              Legal Middle Names (Optional)
            </label>
            <input
              type="text"
              id="middle_name"
              name="middle_name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm"
              placeholder="As it appears on your government ID"
            />
          </div>

          <button
            type="submit"
            disabled={!firstName.trim() || !lastName.trim() || isSubmitting}
            className="w-full bg-blue-600 text-white font-medium py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Legal Name & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};
